/**
 * "Total Value" at the foot of the form (BILL-013): subtotal, each tax
 * "@ rate on ₹base", rounding, total — and any deductions after it, ending in
 * the net total (BILL-017). The figures are the server's, as of the last save.
 *
 * In a draft the rounding carries a pencil (BILL-014): the operator may type
 * the rounding the supplier's or customer's printed total used, within a
 * rupee. It is kept with the form and applied when the draft is saved.
 */
import { useState } from 'react'
import { formatINR, type TotalValue } from '@project/contracts'
import { Button, Money, Panel } from '../design/index.js'

export interface TotalValuePanelProps {
  readonly value: TotalValue
  /** Draft and editable: the rounding may be typed over. */
  readonly editable: boolean
  /** What has been typed for the rounding but not yet saved, if anything. */
  readonly typed: string
  readonly onRounding: (value: string) => void
}

export const TotalValuePanel = ({ value, editable, typed, onRounding }: TotalValuePanelProps) => {
  const [editing, setEditing] = useState(false)
  return (
    <Panel title="Total value" aside="As of the last save">
      <table className="totals" aria-label="Total value">
        <tbody>
          <tr><td>Subtotal</td><td><Money value={value.subtotal} /></td></tr>
          {value.taxes.map((t) => (
            <tr key={`${t.kind}-${t.pct}`}>
              <td>{`${t.kind} @ ${t.pct}% on ${formatINR(t.base)}`}</td>
              <td><Money value={t.amount} /></td>
            </tr>
          ))}
          <tr>
            <td>
              Rounding adjustment
              {value.roundingOverridden ? <span className="muted"> (typed)</span> : null}
              {editable && !editing ? (
                <button type="button" className="link-button" style={{ marginLeft: 8 }}
                  aria-label="Change the rounding adjustment" onClick={() => setEditing(true)}>
                  ✎
                </button>
              ) : null}
            </td>
            <td>
              {editing ? (
                <span className="row" style={{ gap: 6, justifyContent: 'flex-end' }}>
                  <input
                    className="control control--right"
                    style={{ width: 110 }}
                    inputMode="decimal"
                    aria-label="Rounding adjustment"
                    value={typed !== '' ? typed : value.rounding.replace(/0+$/, '').replace(/\.$/, '')}
                    onChange={(e) => onRounding(e.currentTarget.value)}
                  />
                  <Button kind="ghost" onClick={() => { onRounding(''); setEditing(false) }}>Use computed</Button>
                </span>
              ) : <Money value={value.rounding} />}
            </td>
          </tr>
          <tr className="is-total"><td>Total</td><td><Money value={value.total} size={17} /></td></tr>
          {value.adjustments.map((a, i) => (
            <tr key={`${a.label}-${i}`}><td>{a.label}</td><td><Money value={a.amount} /></td></tr>
          ))}
          {value.adjustments.length > 0 ? (
            <tr className="is-total"><td>Net total</td><td><Money value={value.netTotal} size={17} /></td></tr>
          ) : null}
        </tbody>
      </table>
      {editing ? (
        <p className="helper" style={{ marginTop: 8 }}>
          At most ₹1.00 either way. It replaces the computed rounding when you save.
        </p>
      ) : null}
    </Panel>
  )
}
